import React, { useState, useEffect } from 'react';
import { 
  Settings, Mail, Server, Save, Trash2, Plus, Check, X, Loader2,
  Eye, EyeOff, RefreshCw, FileText, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { atencionClienteAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ConfiguracionAtencionCliente = () => {
  const [activeTab, setActiveTab] = useState('smtp');
  
  // Config Email
  const [configsEmail, setConfigsEmail] = useState([]);
  const [loadingEmail, setLoadingEmail] = useState(true);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  
  const [emailForm, setEmailForm] = useState({
    nombre: '',
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: false,
    smtp_use_ssl: true,
    email_from: '',
    nombre_from: 'LEXDUO'
  });

  // Plantillas
  const [plantillas, setPlantillas] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);
  const [isPlantillaDialogOpen, setIsPlantillaDialogOpen] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [savingPlantilla, setSavingPlantilla] = useState(false);
  
  const [plantillaForm, setPlantillaForm] = useState({
    nombre: '',
    descripcion: '',
    asunto: '',
    cuerpo: '',
    activa: true,
    orden: 0
  });

  useEffect(() => {
    loadConfigsEmail();
    loadPlantillas();
  }, []);

  const loadConfigsEmail = async () => {
    setLoadingEmail(true);
    try {
      // Primero intentar obtener o crear la configuración por defecto
      await atencionClienteAPI.getDefaultConfig();
      // Luego cargar todas las configuraciones
      const res = await atencionClienteAPI.getConfigEmail();
      setConfigsEmail(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo cargar la configuración', variant: 'destructive' });
    } finally {
      setLoadingEmail(false);
    }
  };

  const loadPlantillas = async () => {
    setLoadingPlantillas(true);
    try {
      const res = await atencionClienteAPI.getPlantillas();
      setPlantillas(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron cargar las plantillas', variant: 'destructive' });
    } finally {
      setLoadingPlantillas(false);
    }
  };

  // ================== Email Config ==================
  const resetEmailForm = () => {
    setEmailForm({
      nombre: '',
      smtp_host: '',
      smtp_port: 465,
      smtp_user: '',
      smtp_password: '',
      smtp_use_tls: false,
      smtp_use_ssl: true,
      email_from: '',
      nombre_from: 'LEXDUO'
    });
    setEditingConfig(null);
    setShowPassword(false);
  };

  const handleOpenNewEmail = () => {
    resetEmailForm();
    setIsEmailDialogOpen(true);
  };

  const handleEditEmail = (config) => {
    setEmailForm({
      nombre: config.nombre || '',
      smtp_host: config.smtp_host || '',
      smtp_port: config.smtp_port || 465,
      smtp_user: config.smtp_user || '',
      smtp_password: '', // No mostrar la contraseña real
      smtp_use_tls: config.smtp_use_tls || false,
      smtp_use_ssl: config.smtp_use_ssl || true,
      email_from: config.email_from || '',
      nombre_from: config.nombre_from || 'LEXDUO'
    });
    setEditingConfig(config);
    setIsEmailDialogOpen(true);
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.smtp_host || !emailForm.smtp_user || !emailForm.email_from) {
      toast({ title: 'Error', description: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }
    
    setSavingEmail(true);
    try {
      const data = { ...emailForm };
      // Si está editando y no cambió la contraseña, no enviarla
      if (editingConfig && !data.smtp_password) {
        delete data.smtp_password;
      }
      
      if (editingConfig) {
        await atencionClienteAPI.updateConfigEmail(editingConfig.id, data);
        toast({ title: 'Configuración actualizada' });
      } else {
        await atencionClienteAPI.createConfigEmail(data);
        toast({ title: 'Configuración creada' });
      }
      
      setIsEmailDialogOpen(false);
      resetEmailForm();
      loadConfigsEmail();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleDeleteEmail = async (config) => {
    if (!window.confirm('¿Está seguro de eliminar esta configuración?')) return;
    try {
      await atencionClienteAPI.deleteConfigEmail(config.id);
      toast({ title: 'Configuración eliminada' });
      loadConfigsEmail();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleTestEmail = async (config) => {
    setTesting(true);
    try {
      const res = await atencionClienteAPI.testConfigEmail(config.id);
      if (res.data.success) {
        toast({ title: '¡Prueba exitosa!', description: 'El email de prueba se envió correctamente' });
        loadConfigsEmail();
      } else {
        toast({ title: 'Error de envío', description: res.data.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo enviar el email de prueba', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleToggleActivo = async (config) => {
    try {
      await atencionClienteAPI.updateConfigEmail(config.id, { activo: !config.activo });
      loadConfigsEmail();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  // ================== Plantillas ==================
  const resetPlantillaForm = () => {
    setPlantillaForm({
      nombre: '',
      descripcion: '',
      asunto: '',
      cuerpo: '',
      activa: true,
      orden: 0
    });
    setEditingPlantilla(null);
  };

  const handleOpenNewPlantilla = () => {
    resetPlantillaForm();
    setIsPlantillaDialogOpen(true);
  };

  const handleEditPlantilla = (plantilla) => {
    setPlantillaForm({
      nombre: plantilla.nombre || '',
      descripcion: plantilla.descripcion || '',
      asunto: plantilla.asunto || '',
      cuerpo: plantilla.cuerpo || '',
      activa: plantilla.activa !== false,
      orden: plantilla.orden || 0
    });
    setEditingPlantilla(plantilla);
    setIsPlantillaDialogOpen(true);
  };

  const handleSubmitPlantilla = async (e) => {
    e.preventDefault();
    if (!plantillaForm.nombre || !plantillaForm.asunto || !plantillaForm.cuerpo) {
      toast({ title: 'Error', description: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }
    
    setSavingPlantilla(true);
    try {
      if (editingPlantilla) {
        await atencionClienteAPI.updatePlantilla(editingPlantilla.id, plantillaForm);
        toast({ title: 'Plantilla actualizada' });
      } else {
        await atencionClienteAPI.createPlantilla(plantillaForm);
        toast({ title: 'Plantilla creada' });
      }
      
      setIsPlantillaDialogOpen(false);
      resetPlantillaForm();
      loadPlantillas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSavingPlantilla(false);
    }
  };

  const handleDeletePlantilla = async (plantilla) => {
    if (!window.confirm('¿Está seguro de eliminar esta plantilla?')) return;
    try {
      await atencionClienteAPI.deletePlantilla(plantilla.id);
      toast({ title: 'Plantilla eliminada' });
      loadPlantillas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center">
          <Settings className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Configuración Atención Cliente</h1>
          <p className="text-gray-600">Gestión de servidores SMTP y plantillas de email</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            Servidor SMTP
          </TabsTrigger>
          <TabsTrigger value="plantillas" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Plantillas Email
          </TabsTrigger>
        </TabsList>

        {/* ================ TAB SMTP ================ */}
        <TabsContent value="smtp" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-600">Configure el servidor SMTP para enviar emails a los clientes</p>
            <Button onClick={handleOpenNewEmail} className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Configuración
            </Button>
          </div>

          {loadingEmail ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : configsEmail.length > 0 ? (
            <div className="grid gap-4">
              {configsEmail.map((config) => (
                <Card key={config.id} className={!config.activo ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.activo ? 'bg-cyan-100' : 'bg-gray-100'}`}>
                          <Mail className={`w-5 h-5 ${config.activo ? 'text-cyan-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {config.nombre}
                            {config.verificado && (
                              <Badge className="bg-green-100 text-green-700">
                                <Check className="w-3 h-3 mr-1" /> Verificado
                              </Badge>
                            )}
                            {!config.activo && (
                              <Badge className="bg-gray-100 text-gray-500">Inactivo</Badge>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600">{config.email_from}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {config.smtp_host}:{config.smtp_port} 
                            ({config.smtp_use_ssl ? 'SSL' : config.smtp_use_tls ? 'TLS' : 'Sin cifrado'})
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={config.activo}
                          onCheckedChange={() => handleToggleActivo(config)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestEmail(config)}
                          disabled={testing}
                        >
                          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditEmail(config)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteEmail(config)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Server className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No hay servidores SMTP configurados</p>
                <Button variant="outline" className="mt-4" onClick={handleOpenNewEmail}>
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir configuración
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ================ TAB PLANTILLAS ================ */}
        <TabsContent value="plantillas" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-600">Plantillas predefinidas para respuestas a clientes</p>
              <p className="text-sm text-gray-400 mt-1">
                Variables disponibles: {"{{cliente_nombre}}"}, {"{{expediente_numero}}"}
              </p>
            </div>
            <Button onClick={handleOpenNewPlantilla} className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Plantilla
            </Button>
          </div>

          {loadingPlantillas ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : plantillas.length > 0 ? (
            <div className="grid gap-4">
              {plantillas.map((plantilla) => (
                <Card key={plantilla.id} className={!plantilla.activa ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          {plantilla.nombre}
                          {!plantilla.activa && (
                            <Badge className="bg-gray-100 text-gray-500">Inactiva</Badge>
                          )}
                        </h3>
                        {plantilla.descripcion && (
                          <p className="text-sm text-gray-500">{plantilla.descripcion}</p>
                        )}
                        <p className="text-sm text-gray-600 mt-2">
                          <span className="font-medium">Asunto:</span> {plantilla.asunto}
                        </p>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {plantilla.cuerpo}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditPlantilla(plantilla)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletePlantilla(plantilla)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No hay plantillas configuradas</p>
                <Button variant="outline" className="mt-4" onClick={handleOpenNewPlantilla}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear plantilla
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ================ DIALOG EMAIL CONFIG ================ */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              {editingConfig ? 'Editar Configuración SMTP' : 'Nueva Configuración SMTP'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitEmail} className="space-y-4">
            <div>
              <Label>Nombre de la configuración</Label>
              <Input
                value={emailForm.nombre}
                onChange={(e) => setEmailForm({...emailForm, nombre: e.target.value})}
                placeholder="Ej: Atención Cliente"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Servidor SMTP *</Label>
                <Input
                  value={emailForm.smtp_host}
                  onChange={(e) => setEmailForm({...emailForm, smtp_host: e.target.value})}
                  placeholder="smtp.ejemplo.com"
                  required
                />
              </div>
              <div>
                <Label>Puerto *</Label>
                <Input
                  type="number"
                  value={emailForm.smtp_port}
                  onChange={(e) => setEmailForm({...emailForm, smtp_port: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Usuario SMTP *</Label>
                <Input
                  value={emailForm.smtp_user}
                  onChange={(e) => setEmailForm({...emailForm, smtp_user: e.target.value})}
                  placeholder="usuario@ejemplo.com"
                  required
                />
              </div>
              <div>
                <Label>Contraseña {editingConfig ? '' : '*'}</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={emailForm.smtp_password}
                    onChange={(e) => setEmailForm({...emailForm, smtp_password: e.target.value})}
                    placeholder={editingConfig ? '(sin cambios)' : '********'}
                    required={!editingConfig}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="ssl"
                  checked={emailForm.smtp_use_ssl}
                  onCheckedChange={(v) => setEmailForm({...emailForm, smtp_use_ssl: v, smtp_use_tls: v ? false : emailForm.smtp_use_tls})}
                />
                <Label htmlFor="ssl">Usar SSL</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="tls"
                  checked={emailForm.smtp_use_tls}
                  onCheckedChange={(v) => setEmailForm({...emailForm, smtp_use_tls: v, smtp_use_ssl: v ? false : emailForm.smtp_use_ssl})}
                />
                <Label htmlFor="tls">Usar TLS</Label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email remitente *</Label>
                <Input
                  type="email"
                  value={emailForm.email_from}
                  onChange={(e) => setEmailForm({...emailForm, email_from: e.target.value})}
                  placeholder="info@lexduo.cat"
                  required
                />
              </div>
              <div>
                <Label>Nombre remitente</Label>
                <Input
                  value={emailForm.nombre_from}
                  onChange={(e) => setEmailForm({...emailForm, nombre_from: e.target.value})}
                  placeholder="LEXDUO"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={savingEmail}>
                {savingEmail && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ================ DIALOG PLANTILLA ================ */}
      <Dialog open={isPlantillaDialogOpen} onOpenChange={setIsPlantillaDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitPlantilla} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de la plantilla *</Label>
                <Input
                  value={plantillaForm.nombre}
                  onChange={(e) => setPlantillaForm({...plantillaForm, nombre: e.target.value})}
                  placeholder="Ej: Respuesta inicial"
                  required
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={plantillaForm.descripcion}
                  onChange={(e) => setPlantillaForm({...plantillaForm, descripcion: e.target.value})}
                  placeholder="Descripción breve"
                />
              </div>
            </div>

            <div>
              <Label>Asunto del email *</Label>
              <Input
                value={plantillaForm.asunto}
                onChange={(e) => setPlantillaForm({...plantillaForm, asunto: e.target.value})}
                placeholder="Re: Su consulta en LEXDUO"
                required
              />
            </div>

            <div>
              <Label>Cuerpo del email *</Label>
              <Textarea
                value={plantillaForm.cuerpo}
                onChange={(e) => setPlantillaForm({...plantillaForm, cuerpo: e.target.value})}
                placeholder="Estimado/a {{cliente_nombre}},&#10;&#10;Gracias por contactar con LEXDUO..."
                rows={8}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Variables: {"{{cliente_nombre}}"}, {"{{expediente_numero}}"}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="activa"
                checked={plantillaForm.activa}
                onCheckedChange={(v) => setPlantillaForm({...plantillaForm, activa: v})}
              />
              <Label htmlFor="activa">Plantilla activa</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPlantillaDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={savingPlantilla}>
                {savingPlantilla && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionAtencionCliente;
